import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppId } from '../../context/AppIdContext';
import Header from '../../component/header';
import axios from 'axios';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const PartyCader = () => {
    const navigate = useNavigate();
    const { appId, language } = useAppId();
    
    const lang = language || 'en';

    // --- Translation Dictionary ---
    const t = {
        en: {
            pageTitle: "Party Cader",
            saktiKendra: "Sakti Kendra",
            boothPramukh: "Booth Pramukh",
            karyakarta: "Karyakarta",
            mandalPramukh: "Mandal Pramukh",
            panaPramukh: "Pana Pramukh",
            total: "Total",
            allocated: "Allocated",
            pending: "Pending",
            progress: "Allocation Progress",
            back: "Back to Dashboard",
            loading: "Loading..."
        },
        hi: {
            pageTitle: "पार्टी कार्यकर्ता",
            saktiKendra: "शक्ति केंद्र",
            boothPramukh: "बूथ प्रमुख",
            karyakarta: "कार्यकर्ता",
            mandalPramukh: "मंडल प्रमुख",
            panaPramukh: "पाना प्रमुख",
            total: "कुल",
            allocated: "आवंटित",
            pending: "लंबित",
            progress: "आवंटन प्रगति",
            back: "डैशबोर्ड पर वापस",
            loading: "लोड हो रहा है..."
        },
        gu: {
            pageTitle: "પાર્ટી કાર્યકર્તા",
            saktiKendra: "શક્તિ કેન્દ્ર",
            boothPramukh: "બૂથ પ્રમુખ",
            karyakarta: "કાર્યકર્તા",
            mandalPramukh: "મંડળ પ્રમુખ",
            panaPramukh: "પાના પ્રમુખ",
            total: "કુલ",
            allocated: "ફાળવેલ",
            pending: "બાકી",
            progress: "ફાળવણી પ્રગતિ",
            back: "ડેશબોર્ડ પર પાછા",
            loading: "લોડ થઈ રહ્યું છે..."
        }
    };

    const txt = t[lang] || t.en;

    // --- State for Data ---
    const [loading, setLoading] = useState(true);
    const [caderData, setCaderData] = useState({
        saktiKendra: { total: 0 },
        boothPramukh: { total: 0, allocated: 0, pending: 0 },
        karyakarta: { total: 0 },
        mandalPramukh: { total: 8900 }, // Static value
        panaPramukh: { total: 23400 }   // Static value
    });

    // --- Fetch API Data ---
    useEffect(() => {
        if (appId) {
            fetchPartyCadreData();
        }
    }, [appId]);

    const fetchPartyCadreData = async () => {
        setLoading(true);
        try {
            const response = await axios.post(
                `${BASE_URL}/api/dis_party_cadre_dash`,
                { app_id: appId }
            );

            if (response.data.success && response.data.data && response.data.data.Success === "1") {
                const result = response.data.data.result[0];
                
                setCaderData({
                    saktiKendra: { total: result.total_shaktikendra || 0 },
                    boothPramukh: { 
                        total: result.total_booth || 0,
                        allocated: result.total_booth_with_pramukh || 0,
                        pending: result.remain_booth || 0
                    },
                    karyakarta: { total: result.total_karyakarta || 0 },
                    mandalPramukh: { total: 8900 }, // Static value
                    panaPramukh: { total: 23400 }   // Static value
                });
            }
        } catch (error) {
            console.error("Error fetching party cadre data:", error);
        } finally {
            setLoading(false);
        }
    };

    // Calculate Progress Percentage safely
    const calculateProgress = (allocated, total) => {
        if (!total || total === 0) return 0;
        return Math.round((allocated / total) * 100);
    };

    if (loading) {
        return (
            <div className="party-cader-container">
                <div className="header-wrapper shadow-sm">
                    <Header title={txt.pageTitle} />
                </div>
                <div className="loader-wrapper">
                    <div className="spinner"></div>
                    <p className="loader-text">{txt.loading}</p>
                </div>
                <style jsx>{`
                    .party-cader-container {
                        width: 100%;
                        min-height: 100vh;
                        background: #E5E7EB;
                        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
                    }
                    .header-wrapper {
                        background: white;
                        padding: 0px 24px;
                        position: sticky;
                        top: 0;
                        z-index: 40;
                    }
                    .loader-wrapper {
                        display: flex;
                        flex-direction: column;
                        justify-content: center;
                        align-items: center;
                        min-height: 60vh;
                        width: 100%;
                    }
                    .spinner {
                        width: 50px;
                        height: 50px;
                        border: 4px solid #E5E7EB;
                        border-top: 4px solid #3b82f6;
                        border-radius: 50%;
                        animation: spin 1s linear infinite;
                        margin-bottom: 16px;
                    }
                    .loader-text {
                        color: #6B7280;
                        font-weight: 500;
                        font-size: 1.1rem;
                    }
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                `}</style>
            </div>
        );
    }

    return (
        <div className="party-cader-container">
            {/* Header */}
            <div className="header-wrapper shadow-sm">
                <Header title={txt.pageTitle} />
            </div>

            <div className="content-container p-4 mx-auto" style={{ maxWidth: '1000px' }}>

                <div className="cards-wrapper">
                    {/* --- 1. SAKTI KENDRA CARD (Single Value) --- */}
                    <div className="cader-card slide-up-element" style={{ animationDelay: '0.0s' }} onClick={() => navigate('/dis_saktikendra')}>
                        <div className="card-top-accent sk-accent"></div>
                        
                        <div className="card-inner-padding single-stat-padding">
                            <div className="single-stat-layout">
                                <div className="d-flex align-items-center gap-3">
                                    <div className="title-icon-wrapper sk-icon-bg">
                                        <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                        </svg>
                                    </div>
                                    <h2 className="cader-title mb-0">{txt.saktiKendra}</h2>
                                </div>
                                <div className="single-stat-right">
                                    <span className="stat-number sk-text" style={{ fontSize: '36px', lineHeight: '1' }}>
                                        {caderData.saktiKendra.total.toLocaleString()}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* --- 2. MANDAL PRAMUKH CARD (Single Value - Static) --- */}
                    <div className="cader-card slide-up-element" style={{ animationDelay: '0.1s' }} onClick={() => navigate('#')}>
                        <div className="card-top-accent mp-accent"></div>
                        
                        <div className="card-inner-padding single-stat-padding">
                            <div className="single-stat-layout">
                                <div className="d-flex align-items-center gap-3">
                                    <div className="title-icon-wrapper mp-icon-bg">
                                        <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                                        </svg>
                                    </div>
                                    <h2 className="cader-title mb-0">{txt.mandalPramukh}</h2>
                                </div>
                                <div className="single-stat-right">
                                    <span className="stat-number mp-text" style={{ fontSize: '36px', lineHeight: '1' }}>
                                        {caderData.mandalPramukh.total.toLocaleString()}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    

                    {/* --- 4. BOOTH PRAMUKH CARD (With Progress - From API) --- */}
                    <div className="cader-card slide-up-element" style={{ animationDelay: '0.2s' }} onClick={() => navigate('/booth_pramukh')}>
                        <div className="card-top-accent bp-accent"></div>
                        
                        <div className="card-inner-padding">
                            <div className="card-header-flex">
                                <div className="title-icon-wrapper bp-icon-bg">
                                    <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" />
                                    </svg>
                                </div>
                                <h2 className="cader-title">{txt.boothPramukh}</h2>
                            </div>

                            <div className="stats-grid">
                                <div className="stat-box stat-total">
                                    <span className="stat-label">{txt.total}</span>
                                    <span className="stat-number">{caderData.boothPramukh.total.toLocaleString()}</span>
                                </div>
                                <div className="stat-box stat-allocated">
                                    <span className="stat-label">{txt.allocated}</span>
                                    <span className="stat-number">{caderData.boothPramukh.allocated.toLocaleString()}</span>
                                </div>
                                <div className="stat-box stat-pending">
                                    <span className="stat-label">{txt.pending}</span>
                                    <span className="stat-number">{caderData.boothPramukh.pending.toLocaleString()}</span>
                                </div>
                            </div>

                            <div className="progress-section mt-4">
                                <div className="progress-header">
                                    <span className="progress-title">{txt.progress}</span>
                                    <span className="progress-percentage bp-text">
                                        {calculateProgress(caderData.boothPramukh.allocated, caderData.boothPramukh.total)}%
                                    </span>
                                </div>
                                <div className="progress-track">
                                    <div 
                                        className="progress-fill bp-fill" 
                                        style={{ width: `${calculateProgress(caderData.boothPramukh.allocated, caderData.boothPramukh.total)}%` }}
                                    ></div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* --- 3. PANA PRAMUKH CARD (Single Value - Static) --- */}
                    <div className="cader-card slide-up-element" style={{ animationDelay: '0.15s' }} onClick={() => navigate('#')}>
                        <div className="card-top-accent pp-accent"></div>
                        
                        <div className="card-inner-padding single-stat-padding">
                            <div className="single-stat-layout">
                                <div className="d-flex align-items-center gap-3">
                                    <div className="title-icon-wrapper pp-icon-bg">
                                        <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                        </svg>
                                    </div>
                                    <h2 className="cader-title mb-0">{txt.panaPramukh}</h2>
                                </div>
                                <div className="single-stat-right">
                                    <span className="stat-number pp-text" style={{ fontSize: '36px', lineHeight: '1' }}>
                                        {caderData.panaPramukh.total.toLocaleString()}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                    

                    {/* --- 5. KARYAKARTA CARD (Single Value - From API) --- */}
                    <div className="cader-card slide-up-element" style={{ animationDelay: '0.25s' }} onClick={() => navigate('/display_karyakarta')}>
                        <div className="card-top-accent kar-accent"></div>
                        
                        <div className="card-inner-padding single-stat-padding">
                            <div className="single-stat-layout">
                                <div className="d-flex align-items-center gap-3">
                                    <div className="title-icon-wrapper kar-icon-bg">
                                        <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                        </svg>
                                    </div>
                                    <h2 className="cader-title mb-0">{txt.karyakarta}</h2>
                                </div>
                                <div className="single-stat-right">
                                    <span className="stat-number kar-text" style={{ fontSize: '36px', lineHeight: '1' }}>
                                        {caderData.karyakarta.total.toLocaleString()}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </div>

            <style jsx>{`
                .party-cader-container {
                    width: 100%;
                    min-height: 100vh;
                    background: #E5E7EB;
                    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
                    padding-bottom: 60px;
                }

                .header-wrapper {
                    background: white;
                    padding: 0px 24px;
                    position: sticky;
                    top: 0;
                    z-index: 40;
                }

                .back-btn-modern {
                    display: flex; align-items: center; gap: 8px;
                    padding: 10px 20px; background: #ffffff;
                    border: 1px solid #e2e8f0; border-radius: 12px;
                    color: #475569; font-weight: 600; font-size: 14px;
                    cursor: pointer; transition: all 0.2s ease;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.02);
                }
                .back-btn-modern:hover { 
                    background: #f1f5f9; border-color: #cbd5e1; transform: translateY(-1px); 
                }

                .cards-wrapper {
                    display: flex;
                    flex-direction: column;
                    gap: 24px;
                }

                /* --- Card Styles --- */
                .cader-card {
                    background: #ffffff; 
                    border-radius: 20px; 
                    box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.05), 0 8px 10px -6px rgba(0, 0, 0, 0.01);
                    border: 1px solid #f1f5f9; 
                    transition: all 0.3s ease;
                    position: relative;
                    overflow: hidden;
                    cursor: pointer;
                }
                .cader-card:hover {
                    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.08), 0 10px 10px -5px rgba(0, 0, 0, 0.03);
                    border-color: #e2e8f0;
                    transform: translateY(-2px);
                }

                /* Theme Colors */
                .sk-accent { background: linear-gradient(90deg, #3b82f6 0%, #8b5cf6 100%); }
                .sk-icon-bg { background: #eff6ff; color: #3b82f6; }
                .sk-fill { background: linear-gradient(90deg, #3b82f6 0%, #2563eb 100%); }
                .sk-text { color: #3b82f6; background: #eff6ff; }

                .mp-accent { background: linear-gradient(90deg, #f59e0b 0%, #f97316 100%); }
                .mp-icon-bg { background: #fffbeb; color: #d97706; }
                .mp-text { color: #d97706; background: #fffbeb; }

                .pp-accent { background: linear-gradient(90deg, #10b981 0%, #059669 100%); }
                .pp-icon-bg { background: #ecfdf5; color: #059669; }
                .pp-text { color: #059669; background: #ecfdf5; }

                .bp-accent { background: linear-gradient(90deg, #0ea5e9 0%, #14b8a6 100%); }
                .bp-icon-bg { background: #f0fdfa; color: #0d9488; }
                .bp-fill { background: linear-gradient(90deg, #0ea5e9 0%, #0d9488 100%); }
                .bp-text { color: #0d9488; background: #f0fdfa; }

                .kar-accent { background: linear-gradient(90deg, #ec4899 0%, #f43f5e 100%); }
                .kar-icon-bg { background: #fdf2f8; color: #e11d48; }
                .kar-text { color: #e11d48; background: #fdf2f8; }

                .card-top-accent {
                    position: absolute; top: 0; left: 0; width: 100%; height: 5px;
                }

                .card-inner-padding {
                    padding: 32px;
                }
                
                .single-stat-padding {
                    padding: 24px 32px;
                }

                .card-header-flex {
                    display: flex;
                    align-items: center;
                    gap: 16px;
                    margin-bottom: 32px;
                    padding-bottom: 20px;
                    border-bottom: 1px solid #f1f5f9;
                }

                .title-icon-wrapper {
                    width: 48px;
                    height: 48px;
                    border-radius: 14px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .cader-title {
                    font-size: 24px;
                    font-weight: 800;
                    color: #0f172a;
                    margin: 0;
                }

                /* Single Stat Layout */
                .single-stat-layout {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    flex-wrap: wrap;
                    gap: 16px;
                }
                .single-stat-right {
                    text-align: right;
                }

                /* --- Stats Grid --- */
                .stats-grid {
                    display: grid;
                    grid-template-columns: repeat(3, 1fr);
                    gap: 20px;
                    margin-bottom: 16px;
                }

                .stat-box {
                    padding: 20px;
                    border-radius: 16px;
                    display: flex;
                    flex-direction: column;
                    align-items: flex-start;
                    gap: 8px;
                    border: 1px solid transparent;
                }

                .stat-label {
                    font-size: 13px;
                    font-weight: 700;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }

                .stat-number {
                    font-size: 28px;
                    font-weight: 800;
                    line-height: 1.2;
                }

                /* Individual Stat Colors */
                .stat-total { background: #f8fafc; border-color: #e2e8f0; }
                .stat-total .stat-label { color: #64748b; }
                .stat-total .stat-number { color: #0f172a; }

                .stat-allocated { background: #f0fdf4; border-color: #bbf7d0; }
                .stat-allocated .stat-label { color: #166534; }
                .stat-allocated .stat-number { color: #15803d; }

                .stat-pending { background: #fff7ed; border-color: #fed7aa; }
                .stat-pending .stat-label { color: #9a3412; }
                .stat-pending .stat-number { color: #ea580c; }

                /* --- Progress Bar --- */
                .progress-section {
                    background: #f8fafc;
                    padding: 20px;
                    border-radius: 16px;
                    border: 1px solid #e2e8f0;
                }

                .progress-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 12px;
                }

                .progress-title {
                    font-size: 14px;
                    font-weight: 700;
                    color: #475569;
                }

                .progress-percentage {
                    font-size: 16px;
                    font-weight: 800;
                    padding: 4px 12px;
                    border-radius: 20px;
                }

                .progress-track {
                    width: 100%;
                    height: 12px;
                    background: #e2e8f0;
                    border-radius: 50px;
                    overflow: hidden;
                    box-shadow: inset 0 1px 2px rgba(0,0,0,0.05);
                }

                .progress-fill {
                    height: 100%;
                    border-radius: 50px;
                    transition: width 1s cubic-bezier(0.4, 0, 0.2, 1);
                }

                /* Animation */
                .slide-up-element { 
                    animation: slideUp 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards; 
                }
                
                @keyframes slideUp { 
                    from { opacity: 0; transform: translateY(20px); } 
                    to { opacity: 1; transform: translateY(0); } 
                }

                /* --- Responsive Adjustments --- */
                @media (max-width: 768px) {
                    .stats-grid {
                        grid-template-columns: repeat(3, 1fr);
                        gap: 12px;
                    }

                    .card-inner-padding {
                        padding: 24px 20px;
                    }
                    
                    .single-stat-padding {
                        padding: 20px;
                    }

                    .cader-title {
                        font-size: 20px;
                    }
                    
                    .title-icon-wrapper {
                        width: 40px; height: 40px;
                    }

                    .stat-box {
                        flex-direction: column;
                        align-items: flex-start;
                        justify-content: center;
                        padding: 14px 12px;
                        gap: 6px;
                    }
                    
                    .stat-number {
                        font-size: 22px;
                    }

                    .stat-label {
                        font-size: 11px;
                    }

                    .single-stat-layout {
                        flex-direction: row;
                        flex-wrap: wrap;
                        align-items: center;
                    }
                    .single-stat-right {
                        text-align: right;
                    }
                }

                @media (max-width: 480px) {
                    .stats-grid {
                        gap: 8px;
                    }
                    
                    .stat-box {
                        padding: 10px 8px;
                    }
                    
                    .stat-number {
                        font-size: 18px;
                    }
                    
                    .stat-label {
                        font-size: 9px;
                    }
                    
                    .cader-title {
                        font-size: 18px;
                    }
                    
                    .title-icon-wrapper {
                        width: 36px;
                        height: 36px;
                    }
                    
                    .single-stat-layout {
                        flex-direction: column;
                        align-items: flex-start;
                    }
                    .single-stat-right {
                        text-align: left;
                    }
                }
            `}</style>
        </div>
    );
};

export default PartyCader;